import React from 'react'
import cn from 'classnames'
import { Container, Row, handleImgixUrl } from '@kogk/common'
import { AspectRatioBox, ButtonDep as Button, Icon, useModal, ModalTemplateType, getVideoIDFromUrl } from '..'

export type GalleryImageType = {
  gallery_image: {
    url: string
  }
  image_caption: string
  image_title: string
}

export interface HeroMediaType {
  singleImage: {
    url: string
    alt: string
  }
  caption?: string
  video?: {
    url: string
  }
  gallery?: GalleryImageType[]
  firstOnPage?: boolean
  btnLabel?: string
  smallVideo?: boolean
  containerClass?: string
  onRenderImageComponent?: () => JSX.Element
}

export type VideoContentType = {
  caption?: string
  title?: string
  id: string | null
  service: string | null
  autoPlay?: boolean
  extraParams?: string
}

const Media: React.FC<{
  gallery?: GalleryImageType[]
  video?: VideoContentType | null
  runModal?: (content: any, type: any) => void
  btnLabel: string
}> = ({ children, gallery, video, runModal, btnLabel }) => {
  if (gallery && runModal) {
    return (
      <>
        <AspectRatioBox ratio='widescreen'>{children}</AspectRatioBox>
        <Button
          type='primary'
          className='media__gallery-btn mb-2 mb-lg-3 ml-lg-3'
          label={`${btnLabel} (${gallery.length})`}
          onClick={() => runModal(gallery, 'ImageGallery')}
          aria-label={`${btnLabel}`}
        />
      </>
    )
  } else if (video && runModal) {
    return (
      <button onClick={() => runModal(video, 'Video')} className='w-100' aria-label='Play video'>
        <AspectRatioBox ratio='widescreen'>
          {children}
          <Icon
            style={{ height: '70px', width: '70px' }}
            containerClass='h-100 w-100 completeCenter'
            type='play'
          />
        </AspectRatioBox>
      </button>
    )
  } else {
    // is just a single image
    return <AspectRatioBox ratio='widescreen'>{children}</AspectRatioBox>
  }
}

type ImageProps = {
  image: {
    url: string
    alt: string
  }
  onRenderImageComponent?: () => JSX.Element
}

const Image = ({ image, onRenderImageComponent }: ImageProps) => {
  if (onRenderImageComponent) {
    return onRenderImageComponent()
  }

  return (
    <img
      className='image-rounded'
      alt={image.alt || 'video thumbnail'}
      src={handleImgixUrl(image.url || '/').url()}
    />
  )
}

const HeroMedia = ({
  singleImage,
  gallery,
  video,
  caption,
  firstOnPage,
  btnLabel = 'View more images',
  smallVideo,
  containerClass = '',
  onRenderImageComponent
}: HeroMediaType) => {
  // user needs to upload at least the single image
  const { executeModal } = useModal()
  const runModal = (content: any, type: ModalTemplateType) => {
    executeModal(content, 0, 'col-lg-7 p-0', type)
  }
  if (!singleImage.url) return null
  // valid gallery has at least 1 valid image
  const validGallery = !!(gallery && !!gallery.length && gallery[0].gallery_image.url) ? gallery : undefined
  const validVideo = video?.url
    ? {
        caption,
        ...getVideoIDFromUrl(video.url),
        extraParams: video.url.includes('?h=') ? '?h=' + video.url.split('?h=')[1] : undefined
      }
    : null

  return (
    <Container
      className={cn(
        'hero-media',
        firstOnPage
          ? 'page-slice-spacer__smaller page-slice-spacer__smaller--top-of-page'
          : 'page-slice-spacer__smaller'
      )}
    >
      <Row>
        <div
          className={cn('col-12', {
            'col-lg-8 offset-lg-2': smallVideo
          })}
        >
          <div className={cn('media', containerClass)}>
            <Media runModal={runModal} video={validVideo} gallery={validGallery} btnLabel={btnLabel}>
              <Image image={singleImage} onRenderImageComponent={onRenderImageComponent} />
            </Media>
          </div>
          {caption && <p className='parag parag--sm pt-1 media__caption'>{caption}</p>}
        </div>
      </Row>
    </Container>
  )
}

export default HeroMedia
